import { useState, useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import machineData from "../assets/machineData/machineData";

export default function MachineGallery() {
  return (
    <section className="py-20 bg-blue-200">
    <div>
      {/* ✅ Hero Section */}
      <section className="text-center  max-w-4xl mx-auto px-4 sm:px-6 py-16">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-6">
          Our Advanced Machinery,
          <br className="hidden sm:block" /> Your Reliable Solutions
        </h1>
        <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
          Founded in 1981, Twin Star Industries delivers high-precision fasteners and
          components for automotive, agriculture, aerospace, and lift systems. With
          advanced CNC and TRAUB machinery, global certifications, and a near-zero
          defect record, we ensure reliable and sustainable manufacturing.
        </p>
      </section>

      {/* ✅ Machine Gallery */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-black mb-8 sm:mb-10 text-center md:text-left">
          Take a look at our Machines
        </h2>

        {/* Responsive grid */}
        <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 md:gap-10">
          {machineData.map((p, i) => (
            <HoverCard key={i} project={p} />
          ))}
        </div>
      </section>
    </div>
    </section>
  );
}

// ✅ Hover Card Component
function HoverCard({ project }) {
  const [isHovering, setIsHovering] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect();
    setCursorPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  return (
    <div
      ref={cardRef}
      className="relative group rounded-2xl sm:rounded-3xl h-[500px] overflow-hidden cursor-none"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onMouseMove={handleMouseMove}
    >
      {/* Image */}
      <img
        src={project.image}
        alt={project.title}
        className="w-full max-h-96 sm:h-[400px] md:h-96 object-cover transition-transform duration-500 group-hover:scale-105"
      />

      {/* Tags overlay */}
      <div
        className={`absolute top-3 sm:top-4 right-3 sm:right-4 bg-white/90 backdrop-blur-sm rounded-full px-2 sm:px-3 py-1 sm:py-2 flex gap-1 sm:gap-2 shadow-lg transition-all duration-300 ${
          isHovering
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-4 pointer-events-none"
        }`}
      >
        {project.tags.map((tag, idx) => (
          <Link key={idx} to="/machines">
            <span className="text-xs sm:text-sm font-medium text-black rounded-full bg-blue-200 px-2 sm:px-4 py-1 sm:py-2">
              {tag}
            </span>
          </Link>
        ))}
      </div>

      {/* Custom cursor (hidden on touch devices) */}
      {isHovering && (
        <div
          className="absolute w-12 h-12 sm:w-16 sm:h-16 bg-blue-950 text-white rounded-full flex items-center justify-center pointer-events-none z-30 transition-transform duration-75 max-sm:hidden"
          style={{
            left: cursorPos.x - 24,
            top: cursorPos.y - 24,
          }}
        >
          <ArrowUpRight size={20} className="sm:size-7" />
        </div>
      )}

      {/* Text */}
      <div className="mt-3 sm:mt-4 px-2 sm:px-4">
        <p className="text-xs sm:text-sm font-medium text-gray-500">
          {project.subtitle}
        </p>
        <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-black">
          {project.title}
        </h3>
      </div>
    </div>
  );
}
